import { Link } from "react-router-dom";
import { Shield, Mail, ArrowUpRight } from "lucide-react";

/**
 * Elite-tier multi-column footer for The Floor.
 *
 * Sections:
 *   • Company    — About, Vision, Mission, Core Values, Purpose
 *   • Legal      — Terms, Policy, Privacy
 *   • Support    — Q&R (FAQ), Support Center, Contact
 *   • Strategic  — partner@thefloor.pro CTA, affiliation, copyright
 *
 * Visually anchored by the parent brand "Nachrist Impact Sphere" so the
 * transition between The Floor and its parent feels seamless and prestigious.
 */
const EliteFooter = () => {
  const company = [
    { to: "/legal/about", label: "About Us" },
    { to: "/legal/mission", label: "Vision" },
    { to: "/legal/mission", label: "Mission Statement" },
    { to: "/legal/about", label: "Core Values" },
    { to: "/legal/about", label: "Purpose" },
  ];
  const legal = [
    { to: "/legal/terms", label: "Terms & Conditions" },
    { to: "/legal/responsible-ai", label: "Our Policy" },
    { to: "/legal/privacy", label: "Privacy" },
    { to: "/legal/e-consent", label: "E-Consent" },
  ];
  const support = [
    { to: "/legal/qa", label: "Q&R (FAQ)" },
    { to: "/legal/support", label: "Support Center" },
    { to: "/legal/support", label: "Contact" },
    { to: "/trust-center", label: "Trust Center" },
  ];

  return (
    <footer
      data-testid="elite-footer"
      className="relative border-t border-sovereign-gold/25 bg-gradient-to-b from-background to-secondary/60 px-6 pt-16 pb-8 mt-12"
    >
      <div className="mx-auto max-w-[1200px]">
        {/* Brand strap */}
        <div className="mb-12 flex flex-col items-start gap-3 border-b border-border/50 pb-10 md:flex-row md:items-end md:justify-between">
          <div className="max-w-md">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-sovereign-gold">
              The Floor
            </p>
            <h3 className="mt-1 font-serif text-2xl font-bold leading-tight text-foreground md:text-3xl">
              The ultimate training ground for elite debaters,
              <br className="hidden md:block" /> CEOs, and political leaders.
            </h3>
            <p className="mt-3 max-w-lg text-sm text-muted-foreground">
              A 1-on-1 AI-driven environment for high-stakes preparation —
              presidential debates, executive negotiations, and decisive interviews.
              Powered by real-time <span className="text-foreground">AI Coaching</span> and{" "}
              <span className="text-foreground">AI-generated Polls</span> to refine delivery
              and perform under pressure with precision.
            </p>
          </div>
          <a
            href="mailto:partner@thefloor.pro"
            data-testid="footer-strategic-cta"
            className="group inline-flex items-center gap-2 rounded-full border border-sovereign-gold bg-sovereign-gold/10 px-5 py-3 font-mono text-xs font-bold uppercase tracking-widest text-sovereign-gold transition-all hover:bg-sovereign-gold hover:text-background"
          >
            <Mail className="h-4 w-4" />
            Strategic Inquiries
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </a>
        </div>

        {/* Link columns */}
        <div className="grid grid-cols-2 gap-10 md:grid-cols-4">
          <FooterCol title="Company" items={company} />
          <FooterCol title="Legal & Policy" items={legal} />
          <FooterCol title="Help & Support" items={support} />
          <div>
            <h4 className="mb-4 font-serif text-sm font-bold uppercase tracking-wider text-foreground">
              Strategic Contact
            </h4>
            <p className="text-xs leading-relaxed text-muted-foreground">
              For elites, governments, and organizations:
            </p>
            <a
              href="mailto:partner@thefloor.pro"
              className="mt-2 block break-all font-mono text-sm font-bold text-sovereign-gold underline-offset-4 hover:underline"
            >
              partner@thefloor.pro
            </a>
            <div className="mt-5 flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
              <Shield className="h-3 w-3 text-sovereign-gold" />
              AES-256 · NIST 800-171 · Sovereign Tier
            </div>
          </div>
        </div>

        {/* Affiliation strap */}
        <div className="mt-12 border-t border-border/50 pt-6">
          <div className="flex flex-col items-center gap-2 text-center md:flex-row md:justify-between md:text-left">
            <p className="text-sm text-muted-foreground">
              The Floor is a flagship initiative of the{" "}
              <strong className="font-bold text-sovereign-gold">Nachrist Impact Sphere</strong>.
            </p>
            <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
              © 2026 Les Nachrist. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({
  title,
  items,
}: {
  title: string;
  items: Array<{ to: string; label: string }>;
}) => (
  <div>
    <h4 className="mb-4 font-serif text-sm font-bold uppercase tracking-wider text-foreground">
      {title}
    </h4>
    <ul className="space-y-2.5">
      {items.map((l) => (
        <li key={`${title}-${l.label}`}>
          <Link
            to={l.to}
            className="text-xs text-muted-foreground transition-colors hover:text-sovereign-gold"
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

export default EliteFooter;
